/**
 * Which budget a property's scope-of-work sheet is FROM — the reviewer's
 * source choice (budget-scope-of-work-draws.md § "Lock as final budget",
 * 2026-09-22).
 *
 *   GET .../scope_of_work/{dealId}/budget_sources?subjectPropertyId=
 *       → { candidates: BudgetSourceSelection[] } — budget documents,
 *         feasibility reviews (proposed AND recommended), appraisal
 *         renovation budgets.
 *
 * Two modes, one list:
 *   • "pick"   — Change source. Choosing a candidate (or "Manual") and
 *                confirming hands it back; picking a DOCUMENT source also offers
 *                "Rebuild the sheet from documents" or "Keep my edits".
 *   • "browse" — Source budgets: the same list read-only, so a reviewer can
 *                compare every budget the loan holds (totals, line counts,
 *                dates, multiple versions) before deciding.
 *
 * Built on the form-guide primitives; previews open over the dialog.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import CloseRounded from "@mui/icons-material/CloseRounded";

import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import {
  FormChoiceCard,
  FormFooter,
  FormNote,
  FormPill,
} from "../../../../components/forms/FormPrimitives";
import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { usePreviewOverDialog } from "../../../../hooks/usePreviewOverDialog";
import { filePreviewSetData } from "../../../../redux/reducers/filePreviewReducer";
import { formSx, px } from "../../../../theme/formStyles";
import { useResolveLoanDocument } from "../LoanDrilldownWireAndFundingTab/panes/HudSourceChip";
import { money } from "./budgetDrawsTheme";

export const SOURCE_KIND_LABEL = {
  budget_document: "Budget document",
  feasibility: "Feasibility",
  appraisal: "Appraisal",
  trustpoint: "TrustPoint",
  manual: "Manual",
};

const MANUAL = { kind: "manual", label: "Manual — the sheet as entered" };

/** A stable key for a candidate (the backend sends no id). */
export const sourceKey = (s) =>
  s
    ? [s.kind, s.budgetOid, s.sourceBlobName, s.figure].map((x) => x || "").join("|")
    : "";

/** "CFSI feasibility 2025-10-27 (proposed) · $1,233,650 · 27 lines" */
export const sourceSummary = (s) =>
  s
    ? [
        s.label || SOURCE_KIND_LABEL[s.kind] || "Budget",
        s.statedTotalBudget != null ? money(s.statedTotalBudget) : null,
        s.lineItemCount != null
          ? `${s.lineItemCount} line${s.lineItemCount === 1 ? "" : "s"}`
          : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

const BudgetSourcePicker = ({
  open,
  mode = "pick",
  onClose,
  api,
  dealId,
  subjectPropertyId,
  propertyLabel,
  current,
  locked,
  busy,
  onConfirm, // (source, { rebuild }) => void — pick mode only
}) => {
  const dispatch = useDispatch();
  const resolveDoc = useResolveLoanDocument();
  const { dialogProps } = usePreviewOverDialog(onClose);

  const [candidates, setCandidates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [choice, setChoice] = useState(null);

  const load = useCallback(async () => {
    if (!dealId) return;
    setLoading(true);
    setFailed(false);
    try {
      const res = await api.getBudgetSources(dealId, subjectPropertyId);
      setCandidates(Array.isArray(res?.candidates) ? res.candidates : []);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [api, dealId, subjectPropertyId]);

  // Keyed on the string, not the object — the sheet is re-created on every
  // parent render and an object dep would refetch in a loop.
  const currentKey = sourceKey(current);
  useEffect(() => {
    if (open) {
      setChoice(currentKey || null);
      load();
    }
  }, [open, load, currentKey]);

  const options = useMemo(
    () => (mode === "pick" ? [...(candidates || []), MANUAL] : candidates || []),
    [candidates, mode],
  );
  const chosen = options.find((o) => sourceKey(o) === choice) || null;
  const changed = !!chosen && sourceKey(chosen) !== sourceKey(current);
  const isDoc = chosen && chosen.kind !== "manual" && chosen.kind !== "trustpoint";

  const preview = (e, s) => {
    e.stopPropagation();
    const doc = resolveDoc({ fileStorageName: s.sourceBlobName });
    if (doc) dispatch(filePreviewSetData(doc));
  };

  const pick = mode === "pick";

  return (
    <Dialog
      open={open}
      {...dialogProps}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: formSx.paper }}
    >
      <Box sx={formSx.head}>
        <Flx jb g={2} sx={{ alignItems: "flex-start" }}>
          <Box sx={{ minWidth: 0 }}>
            <Txt sx={formSx.title}>
              {pick ? "Choose the budget source" : "Source budgets"}
            </Txt>
            <Txt sx={formSx.titleHelper}>
              {[
                propertyLabel,
                pick
                  ? "The budget this property's sheet is from. One choice per property, stored with the sheet."
                  : "Every budget the loan holds for this property — compare versions before choosing one.",
              ]
                .filter(Boolean)
                .join(" · ")}
            </Txt>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close"
            sx={{ mt: px(-4), mr: px(-8) }}
          >
            <CloseRounded sx={{ fontSize: 18 }} />
          </IconButton>
        </Flx>
      </Box>

      <DialogContent sx={formSx.shellBody}>
        {pick && locked ? (
          <Box sx={{ mb: 2 }}>
            <FormNote>
              This sheet is locked as the final budget. Unlock it to change its source.
            </FormNote>
          </Box>
        ) : null}

        {loading && !candidates ? (
          <Flx center sx={{ py: 5 }}>
            <CircularProgress size={22} />
          </Flx>
        ) : failed ? (
          <FormNote>The loan's budgets could not be loaded.</FormNote>
        ) : (
          <Flx column g={1}>
            {!candidates?.length ? (
              <FormNote>
                No budget, feasibility or appraisal budget has been extracted for this
                property yet.
              </FormNote>
            ) : null}
            {options.map((s) => {
              const key = sourceKey(s);
              const isCurrent = key === sourceKey(current);
              const canPreview =
                !!s.sourceBlobName &&
                !!resolveDoc({ fileStorageName: s.sourceBlobName });
              const body = (
                <>
                  <Flx jb g={1.5} sx={{ alignItems: "flex-start" }}>
                    <Box sx={{ minWidth: 0 }}>
                      {s.sourceBlobName ? (
                        <Link
                          component="button"
                          type="button"
                          disabled={!canPreview}
                          onClick={(e) => preview(e, s)}
                          sx={{
                            ...formSx.body,
                            fontWeight: 500,
                            textAlign: "left",
                            wordBreak: "break-word",
                          }}
                        >
                          {s.label || s.sourceBlobName}
                        </Link>
                      ) : (
                        <Txt sx={{ ...formSx.body, fontWeight: 500 }}>
                          {s.label || SOURCE_KIND_LABEL[s.kind]}
                        </Txt>
                      )}
                      <Txt sx={{ ...formSx.meta, display: "block", mt: "2px" }}>
                        {[
                          s.documentDate ? `Dated ${formatDate(s.documentDate, s.documentDate)}` : null,
                          s.lineItemCount != null
                            ? `${s.lineItemCount} line${s.lineItemCount === 1 ? "" : "s"}`
                            : null,
                          s.extractorName,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </Txt>
                    </Box>
                    <Flx column sx={{ alignItems: "flex-end", gap: 0.5, flex: "none" }}>
                      {s.statedTotalBudget != null ? (
                        <Txt sx={{ ...formSx.body, ...formSx.money, fontWeight: 600 }}>
                          {money(s.statedTotalBudget)}
                        </Txt>
                      ) : null}
                      <Flx ac g={0.75}>
                        {isCurrent ? <FormPill tone="info">Current source</FormPill> : null}
                        <FormPill>{SOURCE_KIND_LABEL[s.kind] || s.kind}</FormPill>
                        {s.figure ? <FormPill tone="neutral">{s.figure}</FormPill> : null}
                      </Flx>
                    </Flx>
                  </Flx>
                </>
              );
              return pick ? (
                <FormChoiceCard
                  key={key}
                  selected={choice === key}
                  disabled={locked || busy}
                  onClick={() => setChoice(key)}
                >
                  {body}
                </FormChoiceCard>
              ) : (
                <Box key={key} sx={{ ...formSx.choice(false, false), cursor: "default" }}>
                  {body}
                </Box>
              );
            })}
          </Flx>
        )}
      </DialogContent>

      <FormFooter
        status={
          pick && changed && isDoc
            ? "Rebuilding replaces the sheet's lines from the loan's documents; keeping your edits records the source only."
            : null
        }
      >
        <Button variant="text" color="gray" onClick={onClose} sx={formSx.button}>
          {pick ? "Cancel" : "Close"}
        </Button>
        {pick && isDoc ? (
          <Button
            variant="outlined"
            disabled={!changed || locked || busy}
            onClick={() => onConfirm(chosen, { rebuild: true })}
            sx={formSx.button}
          >
            Rebuild from this source
          </Button>
        ) : null}
        {pick ? (
          <Button
            variant="contained"
            disabled={!changed || locked || busy}
            onClick={() => onConfirm(chosen, { rebuild: false })}
            sx={formSx.button}
          >
            {busy ? "Saving…" : isDoc ? "Keep my edits" : "Use this source"}
          </Button>
        ) : null}
      </FormFooter>
    </Dialog>
  );
};

BudgetSourcePicker.propTypes = {
  open: PropTypes.bool,
  mode: PropTypes.oneOf(["pick", "browse"]),
  onClose: PropTypes.func.isRequired,
  api: PropTypes.object.isRequired,
  dealId: PropTypes.string,
  subjectPropertyId: PropTypes.string,
  propertyLabel: PropTypes.string,
  current: PropTypes.object,
  locked: PropTypes.bool,
  busy: PropTypes.bool,
  onConfirm: PropTypes.func,
};

export default BudgetSourcePicker;
